/**
 * Client Dashboard Page
 */
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Dumbbell, 
  LogOut, 
  Calendar,
  TrendingUp,
  Apple,
  CheckSquare,
  PlayCircle,
  Clock,
  Loader2,
  Lock,
  MessageCircle,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useClientDashboard } from "../hooks/useApi";
import { useTodayWorkout } from "../hooks/useSession";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

export default function ClientDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { dashboard, isLoading } = useClientDashboard();
  const { workout: todayWorkout } = useTodayWorkout();
  
  const handleLogout = async () => {
    await logout();
    window.location.href = "/login";
  };
  
  const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Bonjour";
    if (hour < 18) return "Bon après-midi";
    return "Bonsoir";
  };
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-lime-400" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-zinc-950 pb-20">
      <div className="noise-overlay" />
      
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-zinc-800">
        <div className="max-w-lg mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-lime-400/10 rounded-xl flex items-center justify-center">
                <Dumbbell className="w-5 h-5 text-lime-400" />
              </div>
              <span className="font-heading font-bold text-lg text-white">
                MYGYMCOPILOT
              </span>
            </div>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              data-testid="client-logout-button"
              className="text-zinc-400 hover:text-white hover:bg-zinc-800"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-lg mx-auto px-4 py-6">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="show"
          className="space-y-6"
        >
          {/* Welcome */}
          <motion.section variants={itemVariants}>
            <h1 className="font-heading text-3xl font-bold text-white tracking-tight">
              {greeting()}, {dashboard?.user?.first_name || "Champion"}!
            </h1>
            <p className="text-zinc-500 mt-1">
              Prêt pour ton entraînement ?
            </p>
          </motion.section>
          
          {/* Today's Workout */}
          <motion.section variants={itemVariants}>
            <Card className="bg-gradient-to-br from-lime-400/10 to-lime-400/5 border-lime-400/20 overflow-hidden">
              <CardContent className="p-6">
                {todayWorkout ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Badge className={`${
                        todayWorkout.status === 'completed' 
                          ? 'bg-emerald-400/20 text-emerald-400 border-emerald-400/30'
                          : todayWorkout.status === 'in_progress'
                          ? 'bg-amber-400/20 text-amber-400 border-amber-400/30'
                          : 'bg-lime-400/20 text-lime-400 border-lime-400/30'
                      }`}>
                        {todayWorkout.status === 'completed' ? 'Terminée ✓' 
                          : todayWorkout.status === 'in_progress' ? 'En cours...'
                          : 'Séance du jour'}
                      </Badge>
                      <div className="flex items-center gap-1 text-zinc-400 text-sm">
                        <Clock className="w-4 h-4" />
                        ~45 min
                      </div>
                    </div>
                    
                    <h2 className="font-heading text-2xl font-bold text-white">
                      {todayWorkout.template_name}
                    </h2>
                    
                    <p className="text-zinc-400 text-sm">
                      {todayWorkout.exercises_count} exercices
                    </p>
                    
                    <Button 
                      onClick={() => navigate(`/workout/${todayWorkout.id}`)}
                      className={`w-full h-14 font-bold uppercase tracking-wider rounded-full ${
                        todayWorkout.status === 'completed'
                          ? 'bg-zinc-700 hover:bg-zinc-600 text-white'
                          : 'bg-lime-400 hover:bg-lime-500 text-zinc-950'
                      }`}
                      data-testid="start-workout-button"
                    >
                      <PlayCircle className="w-5 h-5 mr-2" />
                      {todayWorkout.status === 'completed' ? 'Voir la séance'
                        : todayWorkout.status === 'in_progress' ? 'Reprendre'
                        : 'Commencer la séance'}
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <div className="w-16 h-16 bg-zinc-800/50 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Calendar className="w-8 h-8 text-zinc-600" />
                    </div>
                    <h3 className="font-semibold text-white mb-1">
                      Repos aujourd'hui
                    </h3>
                    <p className="text-zinc-500 text-sm">
                      Pas de séance programmée. Profitez de votre journée !
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.section>
          
          {/* Quick Access */}
          <motion.section variants={itemVariants}>
            <h2 className="font-heading text-lg font-bold text-white tracking-tight mb-4">
              ACCÈS RAPIDE
            </h2>
            
            <div className="grid grid-cols-2 gap-3">
              <Card 
                className="bg-zinc-900/50 border-zinc-800 hover:border-sky-400/30 transition-colors cursor-pointer"
                onClick={() => navigate("/progress")}
                data-testid="progress-card"
              >
                <CardContent className="p-4 text-center">
                  <div className="w-12 h-12 bg-sky-400/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <TrendingUp className="w-6 h-6 text-sky-400" />
                  </div>
                  <p className="font-medium text-white text-sm">Ma progression</p>
                </CardContent>
              </Card>
              
              {/* Nutrition Card - Shows lock if disabled */}
              {dashboard?.features?.has_nutrition ? (
                <Card 
                  className="bg-zinc-900/50 border-zinc-800 hover:border-emerald-400/30 transition-colors cursor-pointer"
                  onClick={() => navigate("/nutrition")}
                  data-testid="nutrition-card"
                >
                  <CardContent className="p-4 text-center">
                    <div className="w-12 h-12 bg-emerald-400/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                      <Apple className="w-6 h-6 text-emerald-400" />
                    </div>
                    <p className="font-medium text-white text-sm">Nutrition</p>
                  </CardContent>
                </Card>
              ) : (
                <Card 
                  className="bg-zinc-900/30 border-zinc-800/50 cursor-not-allowed opacity-60"
                  data-testid="nutrition-card-locked"
                >
                  <CardContent className="p-4 text-center relative">
                    <div className="absolute top-2 right-2">
                      <Lock className="w-3 h-3 text-zinc-600" />
                    </div>
                    <div className="w-12 h-12 bg-zinc-800/50 rounded-xl flex items-center justify-center mx-auto mb-3">
                      <Apple className="w-6 h-6 text-zinc-600" />
                    </div>
                    <p className="font-medium text-zinc-500 text-sm">Nutrition</p>
                    <p className="text-xs text-zinc-600 mt-1">Premium</p>
                  </CardContent>
                </Card>
              )}
              
              {/* Habits Card - Shows lock if disabled */}
              {dashboard?.features?.has_habits ? (
                <Card 
                  className="bg-zinc-900/50 border-zinc-800 hover:border-amber-400/30 transition-colors cursor-pointer"
                  onClick={() => navigate("/habits")}
                  data-testid="habits-card"
                >
                  <CardContent className="p-4 text-center">
                    <div className="w-12 h-12 bg-amber-400/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                      <CheckSquare className="w-6 h-6 text-amber-400" />
                    </div>
                    <p className="font-medium text-white text-sm">Habitudes</p>
                  </CardContent>
                </Card>
              ) : (
                <Card 
                  className="bg-zinc-900/30 border-zinc-800/50 cursor-not-allowed opacity-60"
                  data-testid="habits-card-locked"
                >
                  <CardContent className="p-4 text-center relative">
                    <div className="absolute top-2 right-2">
                      <Lock className="w-3 h-3 text-zinc-600" />
                    </div>
                    <div className="w-12 h-12 bg-zinc-800/50 rounded-xl flex items-center justify-center mx-auto mb-3">
                      <CheckSquare className="w-6 h-6 text-zinc-600" />
                    </div>
                    <p className="font-medium text-zinc-500 text-sm">Habitudes</p>
                    <p className="text-xs text-zinc-600 mt-1">Premium</p>
                  </CardContent>
                </Card>
              )}
              
              <Card 
                className="bg-zinc-900/50 border-zinc-800 hover:border-purple-400/30 transition-colors cursor-pointer"
                onClick={() => navigate("/checkin")}
                data-testid="checkin-card"
              >
                <CardContent className="p-4 text-center">
                  <div className="w-12 h-12 bg-purple-400/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <Calendar className="w-6 h-6 text-purple-400" />
                  </div>
                  <p className="font-medium text-white text-sm">Bilan Hebdo</p>
                </CardContent>
              </Card>

              {/* Messaging card */}
              {dashboard?.features?.has_messaging ? (
                <Card 
                  className="bg-zinc-900/50 border-zinc-800 hover:border-indigo-400/30 transition-colors cursor-pointer"
                  onClick={() => navigate("/messages")}
                  data-testid="messaging-card"
                >
                  <CardContent className="p-4 text-center">
                    <div className="w-12 h-12 bg-indigo-400/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                      <MessageCircle className="w-6 h-6 text-indigo-400" />
                    </div>
                    <p className="font-medium text-white text-sm">Messages</p>
                  </CardContent>
                </Card>
              ) : (
                <Card 
                  className="bg-zinc-900/50 border-zinc-800 opacity-50 cursor-pointer"
                  onClick={() => navigate("/messages-locked")}
                  data-testid="messaging-locked-card"
                >
                  <CardContent className="p-4 text-center">
                    <div className="w-12 h-12 bg-zinc-800/50 rounded-xl flex items-center justify-center mx-auto mb-3">
                      <Lock className="w-6 h-6 text-zinc-600" />
                    </div>
                    <p className="font-medium text-zinc-500 text-sm">Messages</p>
                    <p className="text-xs text-zinc-600 mt-1">Premium</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </motion.section>
          
          {/* Stats Preview */}
          <motion.section variants={itemVariants}>
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-lg text-white">
                  Cette semaine
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-lime-400">0</p>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider">Séances</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-sky-400">0</p>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider">Exercices</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-amber-400">0</p>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider">Tonnes</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.section>
        </motion.div>
      </main>
    </div>
  );
}
